import { LOCALE_NAMES } from "@repo/shared";
import type { LocaleCode, LocalizedString } from "@repo/shared/types";
import { ChevronDown, Languages } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogCancel,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { translateText } from "@/lib/api/ai";
import { useUserLanguage } from "@/lib/i18n/context";
import { getTranslationStatus, TRANSLATION_STATUS_COLORS } from "@/lib/i18n/translation-status";
import { useT } from "@/lib/i18n/use-t";
import { cn } from "@/lib/utils";
import { T } from "./localized-input.i18n";

type LocalizedInputProps = {
  id: string;
  label: string;
  value: string;
  i18nValue: LocalizedString;
  onChange: (value: string) => void;
  onI18nChange: (i18nValue: LocalizedString) => void;
  supportedLanguages: string[];
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  /** Optional: override the source language of `value`. If not provided, uses userLanguage from context. */
  sourceLanguage?: string;
  /** Use textarea instead of input for multiline text */
  multiline?: boolean;
  /** Number of rows for textarea (default: 3) */
  rows?: number;
};

export function LocalizedInput({
  id,
  label,
  value,
  i18nValue,
  onChange,
  onI18nChange,
  supportedLanguages,
  placeholder,
  required = false,
  disabled = false,
  sourceLanguage,
  multiline = false,
  rows = 3,
}: LocalizedInputProps) {
  const { userLanguage } = useUserLanguage();
  const t = useT(T);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [localI18n, setLocalI18n] = useState<LocalizedString>({});
  const [isTranslating, setIsTranslating] = useState(false);
  const [translationError, setTranslationError] = useState<string | null>(null);

  // The source language of `value` - either explicitly provided or the user's language
  const valueLang = sourceLanguage || userLanguage;

  // Translation languages = all supported languages EXCEPT the one `value` is in
  const translationLangs = supportedLanguages.filter((lang) => lang !== valueLang);

  const hasMultipleLanguages = translationLangs.length > 0;
  const status = getTranslationStatus(i18nValue, translationLangs);

  // `localI18n` only holds anything between opening the dialog and closing it,
  // so reading it while the dialog is shut reports every language missing.
  // Anything derived from the draft goes through `draft`, never `localI18n`.
  const draft = dialogOpen ? localI18n : i18nValue;
  const missingLangs = translationLangs.filter((lang) => !draft[lang as LocaleCode]?.trim());
  const hasSomeTranslations = missingLangs.length < translationLangs.length;

  const handleOpenDialog = () => {
    setLocalI18n({ ...i18nValue });
    setTranslationError(null);
    setDialogOpen(true);
  };

  const handleSave = () => {
    onI18nChange(localI18n);
    setDialogOpen(false);
  };

  // Updater form rather than a spread of the render's `localI18n`:
  // `handleAiTranslate` merges its result the same way, and a keystroke landing
  // after it resolved would otherwise write back the pre-merge draft and drop
  // the translations that just arrived.
  const handleDraftChange = (langCode: string, next: string) => {
    setLocalI18n((prev) => ({ ...prev, [langCode]: next || undefined }));
  };

  const handleAiTranslate = async (mode: "all" | "missing") => {
    if (!value?.trim()) return;

    const langsToTranslate = mode === "all" ? translationLangs : missingLangs;
    if (langsToTranslate.length === 0) return;

    setIsTranslating(true);
    setTranslationError(null);
    try {
      // Use the source language of `value` for translation (not tenant default)
      const result = await translateText(value, valueLang, langsToTranslate);
      if (result.success && result.translations) {
        setLocalI18n((prev) => ({
          ...prev,
          ...result.translations,
        }));
      } else {
        setTranslationError(result.error || t.translationFailed);
      }
    } catch {
      setTranslationError(t.translationFailed);
    } finally {
      setIsTranslating(false);
    }
  };

  return (
    <div className="space-y-2">
      <Label htmlFor={id}>
        {label} {required && <span className="text-destructive">*</span>}
      </Label>
      <div className="flex gap-2">
        {multiline ? (
          <Textarea
            id={id}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            required={required}
            disabled={disabled}
            className="flex-1"
            rows={rows}
          />
        ) : (
          <Input
            id={id}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            required={required}
            disabled={disabled}
            className="flex-1"
          />
        )}
        {hasMultipleLanguages && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className={cn("shrink-0", TRANSLATION_STATUS_COLORS[status])}
            onClick={handleOpenDialog}
            disabled={disabled}
            tooltip={t.manageTranslations}
          >
            <Languages className="h-4 w-4" />
          </Button>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.translations}</DialogTitle>
            <DialogDescription>
              {t.enterTranslationsFor} &quot;{value || label}&quot;
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Primary language (read-only - edit in main form) */}
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">
                {LOCALE_NAMES[valueLang as LocaleCode] || valueLang}
              </Label>
              {multiline ? (
                <Textarea value={value} disabled className="bg-muted" rows={rows} />
              ) : (
                <Input value={value} disabled className="bg-muted" />
              )}
            </div>

            {/* Other languages */}
            {translationLangs.map((langCode) => (
              <div key={langCode} className="space-y-2">
                <Label htmlFor={`${id}-${langCode}`} className="text-sm text-muted-foreground">
                  {LOCALE_NAMES[langCode as LocaleCode] || langCode}
                </Label>
                {multiline ? (
                  <Textarea
                    id={`${id}-${langCode}`}
                    value={localI18n[langCode as LocaleCode] || ""}
                    onChange={(e) => handleDraftChange(langCode, e.target.value)}
                    placeholder={value || t.translationPlaceholder}
                    rows={rows}
                  />
                ) : (
                  <Input
                    id={`${id}-${langCode}`}
                    value={localI18n[langCode as LocaleCode] || ""}
                    onChange={(e) => handleDraftChange(langCode, e.target.value)}
                    placeholder={value || t.translationPlaceholder}
                  />
                )}
              </div>
            ))}
          </div>

          {translationError && <p className="text-sm text-destructive">{translationError}</p>}

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  className="flex items-center gap-2"
                  disabled={!value}
                  loading={isTranslating}
                >
                  {t.aiTranslate}
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                {hasSomeTranslations && missingLangs.length > 0 && (
                  <DropdownMenuItem onClick={() => handleAiTranslate("missing")}>
                    {t.translateMissing} ({missingLangs.length})
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => handleAiTranslate("all")}>
                  {t.translateAll} ({translationLangs.length})
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <div className="flex-1" />
            <DialogCancel>{t.cancel}</DialogCancel>
            <Button type="button" onClick={handleSave}>
              {t.save}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
